import { BaseApiService, API_CONFIG } from './base.service';
import {
  ContractRecipient,
  ContractRecipientCreate,
  ContractRecipientUpdate,
  ContractRecipientList
} from '../../types';

/**
 * Service for managing contract recipients.
 * Provides methods for creating, retrieving, updating, and deleting contract recipients.
 */
export class ContractRecipientService extends BaseApiService {
  /**
   * Creates a new contract recipient.
   * @param recipientData - The contract recipient creation data.
   * @returns A promise that resolves to the created contract recipient.
   */
  async createContractRecipient(recipientData: ContractRecipientCreate): Promise<ContractRecipient> {
    return this.post(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/`, recipientData);
  }

  /**
   * Retrieves a single contract recipient by its ID.
   * @param id - The ID of the contract recipient to retrieve.
   * @returns A promise that resolves to the contract recipient.
   */
  async getContractRecipient(id: string): Promise<ContractRecipient> {
    return this.get(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/${id}`);
  }

  /**
   * Updates an existing contract recipient.
   * @param id - The ID of the contract recipient to update.
   * @param recipientData - The partial contract recipient data to update.
   * @returns A promise that resolves to the updated contract recipient.
   */
  async updateContractRecipient(id: string, recipientData: ContractRecipientUpdate): Promise<ContractRecipient> {
    return this.put(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/${id}`, recipientData);
  }

  /**
   * Deletes a contract recipient by its ID.
   * @param id - The ID of the contract recipient to delete.
   * @param deletedBy - The user who is deleting the recipient.
   */
  async deleteContractRecipient(id: string, deletedBy: string): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/${id}?deleted_by=${encodeURIComponent(deletedBy)}`);
  }

  /**
   * Retrieves all recipients for a given contract.
   * @param contractId - The ID of the contract.
   * @param skip - Number of records to skip (for pagination).
   * @param limit - Number of records to return (for pagination).
   * @returns A promise that resolves to a list of contract recipients.
   */
  async getRecipientsByContract(contractId: string, skip: number = 0, limit: number = 100): Promise<ContractRecipientList> {
    return this.get(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/contract/${contractId}?skip=${skip}&limit=${limit}`);
  }

  /**
   * Retrieves recipients who should receive contract updates.
   * @param contractId - The ID of the contract.
   * @returns A promise that resolves to an array of contract recipients.
   */
  async getContractUpdateRecipients(contractId: string): Promise<ContractRecipient[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/contract/${contractId}/contract-recipients`);
  }

  /**
   * Retrieves recipients who should receive invoice communications.
   * @param contractId - The ID of the contract.
   * @returns A promise that resolves to an array of contract recipients.
   */
  async getInvoiceRecipients(contractId: string): Promise<ContractRecipient[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/contract/${contractId}/invoice-recipients`);
  }

  /**
   * Activates a contract recipient.
   * @param id - The ID of the contract recipient to activate.
   * @param activatedBy - The user who is activating the recipient.
   */
  async activateContractRecipient(id: string, activatedBy: string): Promise<void> {
    await this.post(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/${id}/activate?activated_by=${encodeURIComponent(activatedBy)}`, {});
  }

  /**
   * Deactivates a contract recipient.
   * @param id - The ID of the contract recipient to deactivate.
   * @param deactivatedBy - The user who is deactivating the recipient.
   */
  async deactivateContractRecipient(id: string, deactivatedBy: string): Promise<void> {
    await this.post(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/${id}/deactivate?deactivated_by=${encodeURIComponent(deactivatedBy)}`, {});
  }

  /**
   * Creates multiple contract recipients in bulk.
   * @param recipientsData - Array of contract recipient creation data.
   * @returns A promise that resolves to an array of created contract recipients.
   */
  async bulkCreateContractRecipients(recipientsData: ContractRecipientCreate[]): Promise<ContractRecipient[]> {
    return this.post(`${API_CONFIG.ENDPOINTS.CONTRACT_RECIPIENTS}/bulk`, recipientsData);
  }
}

export const contractRecipientService = new ContractRecipientService();